import React, { useEffect, useRef, useState } from 'react'
import { Button, Form, Input, Modal, notification, Popover, Radio, Row, Select, Space } from 'antd'
import { QuestionCircleTwoTone, SearchOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import { getAddTreatmentPageAsync } from './addTreatmentSlice'
import { submitMemberInterestBatchAsync, submitMemberInterestExceptionAsync } from '../memberBenefitsSlice'
import { EditableList } from '../../../compnents/list/EditableList'
import { RootDispatch } from '../../../app/store'
import styles from '../MembersManagementPage.module.css'
import { TreatmentBillingCategories, BillingCategoryName } from '../../../models/billingCategory'

interface AddTreatmentProps {
  visible: boolean
  onCancel: () => void
  onOk: (selectedRows: any[]) => void
  title?: string
  mode?: 'full' | 'exception'
  billingCategory?: string
  defaultSelectedRowKeys?: React.Key[]
  defaultSelectedRows?: any[]
}

export const AddTreatment = ({
  visible,
  onCancel,
  onOk,
  title = '添加诊疗',
  mode = 'full',
  billingCategory,
  defaultSelectedRowKeys,
  defaultSelectedRows,
}: AddTreatmentProps) => {
  const dispatch = useDispatch<RootDispatch>()
  const [form] = Form.useForm()

  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [pageLoading, setPageLoading] = useState(false)

  const [queries, setQueries] = useState<any>({ size: 10, current: 1 })
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<any[]>([])
  const [submitLoading, setSubmitLoading] = useState(false)
  const dataRef = useRef(data)
  dataRef.current = data

  // 初始化默认选中项（编辑例外回显）
  useEffect(() => {
    if (visible && defaultSelectedRowKeys && defaultSelectedRows) {
      setSelectedRowKeys(defaultSelectedRowKeys)
      setSelectedRows(defaultSelectedRows)
    }
  }, [visible])

  const handleSearch = (): void => {
    const values = form.getFieldsValue()
    const newQueries = {
      ...queries,
      ...values,
      current: 1,
      filterFlag: mode !== 'exception' ? true : undefined,
    }
    // exception模式：使用当前编辑分类的开票项目过滤数据
    if (mode === 'exception' && billingCategory) {
      newQueries.billingCategory = billingCategory
    }
    // exception模式：使用当前编辑分类的开票项目过滤数据
    if (mode === 'exception' && billingCategory) {
      newQueries.billingCategory = billingCategory
    }
    setQueries(newQueries)
  }

  const handleQuery = () => {
    handleSearch()
  }

  // 获取列表
  const getPage = () => {
    setPageLoading(true)
    dispatch(getAddTreatmentPageAsync({
      ...queries,
      ...(mode !== 'exception' ? { filterFlag: true } : {}),
      // exception模式：使用当前编辑分类的开票项目过滤数据
      ...(mode === 'exception' && billingCategory ? { billingCategory } : {}),
    }))
      .then(unwrapResult)
      .then((res: any) => {
        const records: any[] = res?.records || []
        // 合并 defaultSelectedRows 中的 discountType/price
        if (defaultSelectedRows && defaultSelectedRows.length > 0) {
          const defaultMap = new Map<any, any>(defaultSelectedRows.map((r: any) => [r.id, r]))
          records.forEach((r: any) => {
            const def = defaultMap.get(r.id)
            if (def) {
              r.discountType = def.discountType ?? 0
              r.price = def.price ?? null
            }
          })
        }
        setData(records)
        setTotal(res?.total || 0)
      })
      .finally(() => {
        setPageLoading(false)
      })
  }

  // 弹框打开时重置到第一页
  useEffect(() => {
    if (visible) {
      setQueries({ size: 10, current: 1 })
    }
  }, [visible])

  useEffect(() => {
    if (visible) {
      getPage()
    }
  }, [queries, visible])

  const handleOk = () => {
    setSubmitLoading(true)
    setTimeout(() => {
      const currentData = dataRef.current
      // 将当前页data中的编辑数据合并到跨页累积的selectedRows中
      const dataMap = new Map<any, any>(currentData.map((r: any) => [r.id, r]))
      const mergedSelectedRows = selectedRows.filter(Boolean).map((r: any) => {
        const current = dataMap.get(r.id)
        return current ? { ...r, ...current } : r
      })

      if (mode === 'exception') {
        const params = {
          billingCategory,
          memberInterestExceptions: mergedSelectedRows.map((r: any) => ({
            tenantCatalogId: r.id,
            billingCategory,
          })),
        }
        dispatch(submitMemberInterestExceptionAsync(params))
          .then(unwrapResult)
          .then(() => {
            notification.success({ message: '编辑例外成功' })
            onOk([])
            handleCancel()
          })
          .finally(() => setSubmitLoading(false))
        return
      }

      if (mergedSelectedRows.length === 0) {
        notification.warning({ message: '请至少选择一项' })
        setSubmitLoading(false)
        return
      }

      const params = mergedSelectedRows.map((row: any) => ({
        name: row.name,
        tenantCatalogId: row.id,
        catalogType: 1,
        discountType: row.discountType ?? 0,
        price: (row.price == null || row.price === '')
          ? (row.discountType == 1 ? row.retailPrice : 100)
          : row.price,
      }))

      dispatch(submitMemberInterestBatchAsync(params))
        .then(unwrapResult)
        .then(() => {
          notification.success({ message: '添加成功' })
          handleCancel()
        })
        .finally(() => {
          setSubmitLoading(false)
        })
    }, 500)
  }

  const handleCancel = () => {
    setSelectedRowKeys([])
    setSelectedRows([])
    form.resetFields()
    onCancel()
  }

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    onChange: (keys: React.Key[], rows: any[]) => {
      setSelectedRowKeys(keys)
      setSelectedRows((prev) => {
        const map = new Map(prev.filter(Boolean).map((r: any) => [r.id, r]))
        rows.filter(Boolean).forEach((r: any) => map.set(r.id, r))
        return Array.from(map.values()).filter((r: any) => r && keys.includes(r.id))
      })
    },
    onSelectAll: (
      selected: boolean,
      changeRows: any[],
    ) => {
      if (selected) {
        setSelectedRowKeys((prev) => Array.from(new Set([...prev, ...changeRows.map((v: any) => v.id)])))
        setSelectedRows((prev) => {
          const map = new Map(prev.map((r: any) => [r.id, r]))
          changeRows.forEach((r: any) => map.set(r.id, r))
          return Array.from(map.values())
        })
      } else {
        const changeIds = new Set(changeRows.map((v: any) => v.id))
        setSelectedRowKeys((prev) => prev.filter((k) => !changeIds.has(k)))
        setSelectedRows((prev) => prev.filter((r: any) => !changeIds.has(r.id)))
      }
    },
  }

  const columns = [
    {
      title: '序号',
      key: 'no',
      align: 'center' as const,
      width: '3rem',
      render: (_1: any, _2: any, index: number) =>
        `${(queries.current - 1) * queries.size + index + 1}`,
    },
    {
      title: '项目名称',
      dataIndex: 'name',
      align: 'left' as const,
      key: 'name',
      ellipsis: true,
      render: function ProjectNameCell(_: any, r: any) {
        return <span style={{ fontWeight: 'bold' }}>{r.name}</span>
      },
    },
    {
      title: '目录售价',
      dataIndex: 'retailPrice',
      align: 'center' as const,
      key: 'retailPrice',
      width: '8rem',
      ellipsis: true,
      render: (_: any, r: any) =>
        r.retailPrice
          ? parseFloat(r.retailPrice).toFixed(5)
          : '',
    },
    {
      title: '成本价',
      dataIndex: 'costPrice',
      align: 'center' as const,
      key: 'costPrice',
      width: '8rem',
      ellipsis: true,
      render: (_: any, r: any) =>
        r.costPrice >= 0
          ? parseFloat(r.costPrice).toFixed(5)
          : '',
    },
    {
      title: (
        <Space size={4}>
        折扣类型
        <Popover
            content={
            <div style={{ width: '375px' }}>
                <p>折扣价：根据开方时选择的商品的售价进行计算，如果是按照忽略库存或者合并批次开方则会员价=目录售价*折扣；如果是按照批次开方则会员价=该批次售价*折扣；</p>
                <p>特价：选择了特价后开方时固定取会员价格</p>
            </div>

            }
            placement='top'
            title='说明'
        >
            <QuestionCircleTwoTone />
        </Popover>
        </Space>
    ),
      dataIndex: 'discountType',
      align: 'center' as const,
      key: 'discountType',
      width: '10rem',
      className: 'noRowSelect',
      render: function DiscountTypeCell(_: any, r: any) {
        return (
          <Radio.Group
            defaultValue={0}
            value={r.discountType || 0}
            onChange={(e) => {
              setData((prev) =>
                prev.map((item) =>
                  item.id === r.id ? { ...item, discountType: e.target.value, price: null } : item
                )
              )
            }}
          >
            <Radio value={0}>折扣</Radio>
            <Radio value={1}>特价</Radio>
          </Radio.Group>
        )
      },
    },
    {
      title: '会员价',
      dataIndex: 'price',
      align: 'center' as const,
      key: 'price',
      width: '5rem',
      colSpan: 2,
      className: 'noRightBorder noRowSelect',
      editable: (r: any) => selectedRowKeys.includes(r.id),
      rules: (record: any) => [
        {
          required: true,
          message: '请输入会员价',
        },
        record.discountType == 1
          ? {
              pattern: new RegExp(/^(0|[1-9]\d*)(\.\d{1,5})?$/),
              message: '请输入数字，支持小数点后五位',
            }
          : {
              pattern: new RegExp(/^(100|[1-9]?\d)$/),
              message: '折扣请输入0-100的整数',
            },
      ],
      onSave: (prev: any, next: any) => {
        const price = next.price
        const val = Number(price)
        if (prev.discountType == 1) {
          // 特价：不做范围限制
        } else {
          // 折扣：0-100
          if (val < 0 || val > 100) {
            return
          }
        }
        setData((d) =>
          d.map((item) =>
            item.id === prev.id ? { ...item, price: price || null } : item
          )
        )
      },
      render: function PriceCell(_: any, r: any) {
        if (r.price == null) return ''
        const num = parseFloat(r.price)
        const displayValue = r.discountType == 1 ? num.toFixed(5) : String(Math.round(num))
        if (!r.retailPrice || parseFloat(r.retailPrice) <= 0) {
          return displayValue
        }
        const retailPrice = parseFloat(r.retailPrice)
        let popoverContent: string
        if (r.discountType == 1) {
          // 特价：折扣比例 = 会员价 / 目录售价 * 100
          const ratio = Math.round((num / retailPrice) * 100)
          popoverContent = `折扣比例：${ratio}%`
        } else {
          // 折扣：折后价 = 目录售价 * 折扣%
          const discounted = (retailPrice * num / 100).toFixed(5)
          popoverContent = `折后价：${discounted}元`
        }
        return (
          <Popover content={popoverContent} overlayClassName={styles.pricePopover}>
            <span>{displayValue}</span>
          </Popover>
        )
      },
    },
    {
      dataIndex: 'unit',
      align: 'center' as const,
      key: 'unit',
      width: '4rem',
      colSpan: 0,
      className: 'noRowSelect',
      render: function UnitCell(_: any, r: any) {
        const selected = selectedRowKeys.includes(r.id)
        return <span style={{ color: selected ? undefined : '#bbb' }}>{r.discountType == 1 ? '元' : '%'}</span>
      },
    },
  ]

  const fullColumns = mode === 'full' ? columns : columns.filter(
    (col: any) => !['discountType', 'price', 'unit'].includes(col.dataIndex)
  )

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={handleCancel}
      maskClosable={false}
      onOk={handleOk}
      confirmLoading={submitLoading}
      width={1200}
      destroyOnClose
    >
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 16 }}
      >
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Form.Item name="name" style={{ marginBottom: 0 }}>
            <Input
              placeholder="诊疗项目名称"
              allowClear
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
              onChange={handleQuery}
            />
          </Form.Item>
          {mode !== 'exception' && (
            <Form.Item name="billingCategory" style={{ marginBottom: 0 }}>
            <Select
              placeholder="开票项目"
              allowClear
              style={{ width: 160 }}
              onChange={handleQuery}
            >
              {TreatmentBillingCategories.map((v) => (
                <Select.Option value={v} key={v}>
                  {BillingCategoryName(v)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          )}
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">
              查询
            </Button>
          </Form.Item>
        </Form>
        <span>
          已选择 {selectedRowKeys.length} 条
        </span>
      </Row>

      <EditableList
        loading={pageLoading}
        rowKey={(t: any) => t.id}
        bordered
        onRow={(record: any) => ({
          onClick: (e: React.MouseEvent) => {
            // 点击可编辑单元格区域时不触发行选中切换
            const target = e.target as HTMLElement
            if (target.closest('input') || target.closest('textarea') || target.closest('[data-field]') || target.closest('.noRowSelect')) {
              return
            }
            const key = record.id
            setSelectedRowKeys((prev) => {
              if (prev.includes(key)) {
                setSelectedRows((s) => s.filter((r: any) => r.id !== key))
                return prev.filter((k) => k !== key)
              }
              setSelectedRows((s) => [...s, record])
              return [...prev, key]
            })
          },
        })}
        rowSelection={rowSelection}
        page={{
          items: data,
          current: queries?.current || 1,
          size: queries?.size || 10,
          total,
        }}
        onChangePage={(current, pageSize) => {
          setQueries({
            ...queries,
            current,
            size: pageSize,
          })
        }}
        columns={fullColumns}
      />
    </Modal>
  )
}
